/**
 * Mobile Document Scanner Utilities
 * Perspective transform, corner auto-detection, scanner filters, and compression
 */

/**
 * Distance between two 2D points
 */
export const distance = (p1, p2) => {
  return Math.hypot(p1.x - p2.x, p1.y - p2.y);
};

/**
 * Compute initial default 4 corners for an image with safe padding (inset ~8%)
 */
export const getDefaultCorners = (width, height) => {
  const padX = Math.round(width * 0.08);
  const padY = Math.round(height * 0.08);
  return [
    { x: padX, y: padY },                 // Top-Left (0)
    { x: width - padX, y: padY },         // Top-Right (1)
    { x: width - padX, y: height - padY }, // Bottom-Right (2)
    { x: padX, y: height - padY },         // Bottom-Left (3)
  ];
};

/**
 * Heuristic auto-corner detection using edge contrast scanning on a thumbnail canvas
 */
export const detectDocumentCorners = (imageElement) => {
  const imgWidth = imageElement.naturalWidth || imageElement.width;
  const imgHeight = imageElement.naturalHeight || imageElement.height;

  if (!imgWidth || !imgHeight) {
    return getDefaultCorners(1000, 1400);
  }

  // Use a downscaled canvas for fast edge detection
  const scale = Math.min(1, 400 / Math.max(imgWidth, imgHeight));
  const sw = Math.max(100, Math.round(imgWidth * scale));
  const sh = Math.max(100, Math.round(imgHeight * scale));

  const canvas = document.createElement("canvas");
  canvas.width = sw;
  canvas.height = sh;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  ctx.drawImage(imageElement, 0, 0, sw, sh);

  const imgData = ctx.getImageData(0, 0, sw, sh);
  const data = imgData.data;

  // Convert to grayscale luminance
  const gray = new Uint8Array(sw * sh);
  let sumLum = 0;
  for (let i = 0; i < data.length; i += 4) {
    const lum = Math.round(0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]);
    gray[i / 4] = lum;
    sumLum += lum;
  }
  const avgLum = sumLum / (sw * sh);

  // Simple scan from 4 boundaries inward looking for contrast shift (paper boundary)
  const threshold = Math.max(25, avgLum * 0.22);

  const findBoundary = (startX, startY, dx, dy, maxSteps) => {
    let prev = gray[startY * sw + startX];
    for (let step = 1; step < maxSteps; step++) {
      const x = Math.min(sw - 1, Math.max(0, startX + dx * step));
      const y = Math.min(sh - 1, Math.max(0, startY + dy * step));
      const curr = gray[y * sw + x];
      if (Math.abs(curr - prev) > threshold) {
        return { x: x / scale, y: y / scale };
      }
      prev = curr;
    }
    return null;
  };

  const quarterW = Math.round(sw * 0.25);
  const quarterH = Math.round(sh * 0.25);

  const tl = findBoundary(0, 0, 1, 1, Math.min(quarterW, quarterH)) || { x: imgWidth * 0.08, y: imgHeight * 0.08 };
  const tr = findBoundary(sw - 1, 0, -1, 1, Math.min(quarterW, quarterH)) || { x: imgWidth * 0.92, y: imgHeight * 0.08 };
  const br = findBoundary(sw - 1, sh - 1, -1, -1, Math.min(quarterW, quarterH)) || { x: imgWidth * 0.92, y: imgHeight * 0.92 };
  const bl = findBoundary(0, sh - 1, 1, -1, Math.min(quarterW, quarterH)) || { x: imgWidth * 0.08, y: imgHeight * 0.92 };

  return [
    { x: Math.round(tl.x), y: Math.round(tl.y) },
    { x: Math.round(tr.x), y: Math.round(tr.y) },
    { x: Math.round(br.x), y: Math.round(br.y) },
    { x: Math.round(bl.x), y: Math.round(bl.y) },
  ];
};

/**
 * Apply 4-corner perspective warp homography to extract flat document
 * Returns an off-screen HTMLCanvasElement
 */
export const warpPerspective = (imageElement, corners, maxDimension = 1800) => {
  const [p1, p2, p3, p4] = corners; // TL, TR, BR, BL

  // Determine output document width & height based on edges
  const wTop = distance(p1, p2);
  const wBottom = distance(p4, p3);
  let targetWidth = Math.max(wTop, wBottom);

  const hLeft = distance(p1, p4);
  const hRight = distance(p2, p3);
  let targetHeight = Math.max(hLeft, hRight);

  // Bound maximum dimensions for memory safety and fast mobile processing
  if (targetWidth > maxDimension || targetHeight > maxDimension) {
    const factor = maxDimension / Math.max(targetWidth, targetHeight);
    targetWidth = Math.round(targetWidth * factor);
    targetHeight = Math.round(targetHeight * factor);
  } else {
    targetWidth = Math.round(targetWidth);
    targetHeight = Math.round(targetHeight);
  }

  targetWidth = Math.max(100, targetWidth);
  targetHeight = Math.max(100, targetHeight);

  // Source image data on a canvas
  const srcCanvas = document.createElement("canvas");
  const imgW = imageElement.naturalWidth || imageElement.width;
  const imgH = imageElement.naturalHeight || imageElement.height;
  srcCanvas.width = imgW;
  srcCanvas.height = imgH;
  const srcCtx = srcCanvas.getContext("2d", { willReadFrequently: true });
  srcCtx.drawImage(imageElement, 0, 0);
  const srcData = srcCtx.getImageData(0, 0, imgW, imgH);
  const srcPixels = srcData.data;

  // Output destination canvas
  const dstCanvas = document.createElement("canvas");
  dstCanvas.width = targetWidth;
  dstCanvas.height = targetHeight;
  const dstCtx = dstCanvas.getContext("2d", { willReadFrequently: true });
  const dstData = dstCtx.createImageData(targetWidth, targetHeight);
  const dstPixels = dstData.data;

  // Compute 3x3 Projective Matrix mapping [u/W, v/H] in [0,1]^2 to source (x, y)
  const x1 = p1.x, y1 = p1.y;
  const x2 = p2.x, y2 = p2.y;
  const x3 = p3.x, y3 = p3.y;
  const x4 = p4.x, y4 = p4.y;

  const dx1 = x2 - x3;
  const dx2 = x4 - x3;
  const sx = x1 - x2 + x3 - x4;

  const dy1 = y2 - y3;
  const dy2 = y4 - y3;
  const sy = y1 - y2 + y3 - y4;

  let a, b, c, d, e, f, g, h;
  const det = dx1 * dy2 - dy1 * dx2;

  if (Math.abs(det) < 1e-7) {
    // Affine fallback
    a = x2 - x1;
    b = x4 - x1;
    c = x1;
    d = y2 - y1;
    e = y4 - y1;
    f = y1;
    g = 0;
    h = 0;
  } else {
    g = (sx * dy2 - sy * dx2) / det;
    h = (dx1 * sy - dy1 * sx) / det;
    a = x2 - x1 + g * x2;
    b = x4 - x1 + h * x4;
    c = x1;
    d = y2 - y1 + g * y2;
    e = y4 - y1 + h * y4;
    f = y1;
  }

  // Iterate destination grid with bilinear interpolation
  const invW = 1.0 / targetWidth;
  const invH = 1.0 / targetHeight;

  for (let y = 0; y < targetHeight; y++) {
    const v = y * invH;
    const destRowOffset = y * targetWidth * 4;

    for (let x = 0; x < targetWidth; x++) {
      const u = x * invW;
      const den = g * u + h * v + 1.0;
      const srcX = (a * u + b * v + c) / den;
      const srcY = (d * u + e * v + f) / den;

      const px = Math.floor(srcX);
      const py = Math.floor(srcY);

      if (px >= 0 && px < imgW - 1 && py >= 0 && py < imgH - 1) {
        const fx = srcX - px;
        const fy = srcY - py;
        const fx1 = 1.0 - fx;
        const fy1 = 1.0 - fy;

        const idx00 = (py * imgW + px) * 4;
        const idx10 = idx00 + 4;
        const idx01 = ((py + 1) * imgW + px) * 4;
        const idx11 = idx01 + 4;

        const w00 = fx1 * fy1;
        const w10 = fx * fy1;
        const w01 = fx1 * fy;
        const w11 = fx * fy;

        const dstIdx = destRowOffset + x * 4;
        dstPixels[dstIdx] = Math.round(srcPixels[idx00] * w00 + srcPixels[idx10] * w10 + srcPixels[idx01] * w01 + srcPixels[idx11] * w11);
        dstPixels[dstIdx + 1] = Math.round(srcPixels[idx00 + 1] * w00 + srcPixels[idx10 + 1] * w10 + srcPixels[idx01 + 1] * w01 + srcPixels[idx11 + 1] * w11);
        dstPixels[dstIdx + 2] = Math.round(srcPixels[idx00 + 2] * w00 + srcPixels[idx10 + 2] * w10 + srcPixels[idx01 + 2] * w01 + srcPixels[idx11 + 2] * w11);
        dstPixels[dstIdx + 3] = 255;
      } else if (px >= 0 && px < imgW && py >= 0 && py < imgH) {
        const idx = (py * imgW + px) * 4;
        const dstIdx = destRowOffset + x * 4;
        dstPixels[dstIdx] = srcPixels[idx];
        dstPixels[dstIdx + 1] = srcPixels[idx + 1];
        dstPixels[dstIdx + 2] = srcPixels[idx + 2];
        dstPixels[dstIdx + 3] = 255;
      }
    }
  }

  dstCtx.putImageData(dstData, 0, 0);
  return dstCanvas;
};

/**
 * Apply scanner filter patterns to an input canvas
 * @param {HTMLCanvasElement} canvas
 * @param {'magic_color'|'bw'|'grayscale'|'original'} pattern
 * @returns {HTMLCanvasElement}
 */
export const applyScannerFilter = (canvas, pattern = "magic_color") => {
  const width = canvas.width;
  const height = canvas.height;

  const outCanvas = document.createElement("canvas");
  outCanvas.width = width;
  outCanvas.height = height;
  const ctx = outCanvas.getContext("2d", { willReadFrequently: true });
  ctx.drawImage(canvas, 0, 0);

  if (pattern === "original") {
    // Slight auto-contrast normalization
    const imgData = ctx.getImageData(0, 0, width, height);
    const d = imgData.data;
    for (let i = 0; i < d.length; i += 4) {
      d[i] = Math.min(255, Math.round(d[i] * 1.04));
      d[i + 1] = Math.min(255, Math.round(d[i + 1] * 1.04));
      d[i + 2] = Math.min(255, Math.round(d[i + 2] * 1.04));
    }
    ctx.putImageData(imgData, 0, 0);
    return outCanvas;
  }

  const imgData = ctx.getImageData(0, 0, width, height);
  const data = imgData.data;
  const totalPixels = width * height;

  if (pattern === "grayscale") {
    for (let i = 0; i < data.length; i += 4) {
      const lum = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
      // Gentle contrast curve
      const enhanced = lum > 140 ? Math.min(255, lum * 1.08) : Math.max(0, lum * 0.94);
      data[i] = enhanced;
      data[i + 1] = enhanced;
      data[i + 2] = enhanced;
    }
    ctx.putImageData(imgData, 0, 0);
    return outCanvas;
  }

  if (pattern === "bw") {
    // High-contrast clean black and white document (photocopy effect)
    let sumLum = 0;
    for (let i = 0; i < data.length; i += 4) {
      sumLum += 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    }
    const avgLum = sumLum / totalPixels;
    const threshold = Math.min(195, Math.max(105, avgLum * 0.92));

    for (let i = 0; i < data.length; i += 4) {
      const lum = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
      const val = lum < threshold ? 0 : 255;
      data[i] = val;
      data[i + 1] = val;
      data[i + 2] = val;
    }
    ctx.putImageData(imgData, 0, 0);
    return outCanvas;
  }

  if (pattern === "magic_color") {
    // CamScanner-style "Magic Color":
    // Whitens paper background while making ink/stamps/signatures sharp and saturated
    let sumLum = 0;
    for (let i = 0; i < data.length; i += 4) {
      sumLum += 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    }
    const avgLum = sumLum / totalPixels;
    const paperThreshold = Math.max(120, avgLum * 0.85);

    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const lum = 0.299 * r + 0.587 * g + 0.114 * b;

      if (lum >= paperThreshold) {
        // Light paper area -> stretch toward pure clean white
        const factor = 1.0 + ((lum - paperThreshold) / (255 - paperThreshold)) * 0.45;
        data[i] = Math.min(255, Math.round(r * factor + 12));
        data[i + 1] = Math.min(255, Math.round(g * factor + 12));
        data[i + 2] = Math.min(255, Math.round(b * factor + 12));
      } else {
        // Dark ink / stamp text area -> enrich contrast & depth
        const darkFactor = 0.88;
        data[i] = Math.max(0, Math.round(r * darkFactor));
        data[i + 1] = Math.max(0, Math.round(g * darkFactor));
        data[i + 2] = Math.max(0, Math.round(b * darkFactor));
      }
    }
    ctx.putImageData(imgData, 0, 0);
    return outCanvas;
  }

  return outCanvas;
};

/**
 * Rotate canvas clockwise by angle (90, 180, 270)
 */
export const rotateCanvas = (canvas, angleDegrees = 90) => {
  const angle = ((angleDegrees % 360) + 360) % 360;
  if (angle === 0) return canvas;

  const outCanvas = document.createElement("canvas");
  const isSwap = angle === 90 || angle === 270;
  outCanvas.width = isSwap ? canvas.height : canvas.width;
  outCanvas.height = isSwap ? canvas.width : canvas.height;

  const ctx = outCanvas.getContext("2d");
  ctx.translate(outCanvas.width / 2, outCanvas.height / 2);
  ctx.rotate((angle * Math.PI) / 180);
  ctx.drawImage(canvas, -canvas.width / 2, -canvas.height / 2);

  return outCanvas;
};

/**
 * Compress canvas to a standard lightweight JPEG File object (~180-350KB)
 * @param {HTMLCanvasElement} canvas
 * @param {string} fileName
 * @param {number} quality 0.0 to 1.0 (default 0.82)
 * @returns {Promise<File>}
 */
export const canvasToCompressedFile = (canvas, fileName = "scanned_document.jpg", quality = 0.82) => {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          return reject(new Error("Failed to create blob from canvas"));
        }
        const cleanName = fileName.replace(/\.[^/.]+$/, "") + ".jpg";
        const file = new File([blob], cleanName, {
          type: "image/jpeg",
          lastModified: Date.now(),
        });
        resolve(file);
      },
      "image/jpeg",
      quality
    );
  });
};

/**
 * Format bytes to readable string (e.g. 245 KB)
 */
export const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return "0 KB";
  const k = 1024;
  if (bytes < k * k) {
    return Math.round(bytes / k) + " KB";
  }
  return (bytes / (k * k)).toFixed(1) + " MB";
};
